import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, ScrollText, Gavel, ShieldCheck, Heart, Smartphone, Download } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import DynamicText from "@/components/DynamicText";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlag } from "@/context/FeatureFlagsContext";

function useCountUp(target, duration = 1800) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const loop = (t) => {
      const progress = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const daysOfLife = useCountUp(152340);
  const countries = useCountUp(12);
  const documents = useCountUp(2847);

  const goStart = () => {
    if (user && typeof user === "object") navigate("/app/dashboard");
    else navigate("/register");
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground" data-testid="landing-page">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-[0.25] dark:opacity-[0.18]" />
      <div className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full bg-brand-blue/10 blur-3xl dark:bg-brand-gold/10" />
      <div className="pointer-events-none absolute -right-32 top-40 h-80 w-80 rounded-full bg-brand-gold/10 blur-3xl" />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-10">
        <div className="flex items-center gap-3">
          <Logo size={40} />
          <div className="leading-tight">
            <div className="text-lg font-bold tracking-tight">
              VITAE-PUBLICA<span className="text-xs align-top">™</span>
            </div>
            <div className="text-[11px] italic text-muted-foreground">
              Les Finances Publiques sont des Tranches de Vies
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link to="/about">
            <Button variant="ghost" data-testid="landing-about-btn">
              À propos
            </Button>
          </Link>
          <Link to="/contact">
            <Button variant="ghost" data-testid="landing-contact-btn">
              Contact
            </Button>
          </Link>
          {user && typeof user === "object" ? (
            <Link to="/app/dashboard">
              <Button variant="default" data-testid="landing-go-dashboard">
                Mon tableau de bord
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" data-testid="landing-login-btn">
                  Connexion
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="default" data-testid="landing-register-btn">
                  Créer un compte
                </Button>
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 pt-10 pb-16 md:grid-cols-12 md:px-10 md:pt-20">
        <div className="md:col-span-7 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-accent/30 px-3 py-1 text-xs font-medium text-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-gold" />
            Théorie vitaliste des finances publiques — ELY Mustapha, PMP · I-PMP
          </div>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            L'unité de compte des finances publiques<br />
            n'est pas monnaie...<br />
            <span className="text-brand-blue dark:text-brand-gold">mais vie humaine.</span>
          </h1>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-12 md:items-start">
            {/* NOUVEAU BLOC — Téléchargement Vita-mea (à gauche du livre) */}
            <div className="md:col-span-5">
              <div
                className="rounded-2xl border border-brand-gold/40 bg-gradient-to-br from-amber-50/60 to-card/70 p-4 shadow-sm dark:from-amber-900/10"
                data-testid="landing-vitamea-block"
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-brand-blue dark:text-brand-gold">
                  <Smartphone className="h-4 w-4" aria-hidden="true" />
                  Téléchargez l'application Vita-mea
                </div>
                <p className="mt-1 text-xs leading-relaxed text-foreground/85">
                  Vos indicateurs <strong>valeur-vie</strong> en poche — pour
                  défendre vos droits dans la gestion des finances publiques.
                </p>

                <img
                  src="/assets/vita-mea-splash.jpeg"
                  alt="Vita-mea — Combien de jours de votre vie donnez-vous à l'État ?"
                  data-testid="landing-vitamea-splash"
                  className="mx-auto mt-3 h-auto max-h-[210px] w-auto rounded-xl shadow-md"
                  loading="lazy"
                />

                <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                  Vita-mea est l'application mobile compagnon de
                  VITAE-PUBLICA™. Elle convertit chaque prélèvement (impôt,
                  TVA, cotisations, taxes…) en <em>jours de vie sacrifiés</em>,
                  affiche votre <strong>jour de libération fiscale</strong> et
                  vous fait découvrir, en un geste, le poids vital réel de
                  votre contribution à l'État.
                </p>

                <a
                  href="https://expo.dev/accounts/kafernaum/projects/vita-mea/builds/c20f31d9-6ecc-4216-9e1c-b14aa9ae591e"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="landing-vitamea-download"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-blue px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:bg-brand-gold dark:text-background"
                >
                  <Download className="h-4 w-4" />
                  Télécharger Vita-mea
                </a>

                <div className="mt-4 flex flex-col items-center">
                  <img
                    src="/assets/vita-mea-qr.png"
                    alt="QR Code de téléchargement Vita-mea"
                    data-testid="landing-vitamea-qr"
                    className="h-32 w-32 rounded-md border border-border bg-white p-1.5"
                    loading="lazy"
                  />
                  <span className="mt-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    Scannez pour télécharger
                  </span>
                </div>
              </div>
            </div>

            {/* Colonne image du livre */}
            <div className="md:col-span-3">
              <img
                src="/assets/recto.png"
                alt="Finances Publiques — Une Nouvelle Théorie, Ahmed Ely Mustapha"
                data-testid="landing-book-cover"
                className="mx-auto h-auto w-full max-w-[260px] rounded-xl shadow-md"
                loading="lazy"
              />
            </div>

            {/* Colonne droite : citation + CTAs */}
            <div className="md:col-span-4">
              <blockquote
                className="border-l-4 border-brand-gold bg-card/60 px-5 py-4 text-base italic text-foreground/85 shadow-sm backdrop-blur-sm"
                data-testid="landing-citation"
              >
                « Tout impôt, toute taxe, toute contribution, tout prélèvement obligatoire
                est une amputation de temps de vie consenti par le contribuable à la
                puissance publique. Dès lors, la dilapidation d'un franc public équivaut
                à la dilapidation d'une fraction de vie humaine. »
                <footer className="mt-2 text-xs not-italic text-muted-foreground">
                  — <span className="font-semibold">VITAE-PUBLICA™</span>, manifeste fondateur
                </footer>
              </blockquote>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button
                  size="lg"
                  onClick={goStart}
                  data-testid="landing-cta-start"
                  className="h-12 px-6 text-base font-semibold"
                >
                  <DynamicText k="hero_cta" fallback="Commencer une analyse" />
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Link to="/login">
                  <Button
                    size="lg"
                    variant="outline"
                    data-testid="landing-cta-login"
                    className="h-12 px-6 text-base"
                  >
                    J'ai déjà un compte
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stat card */}
        <div className="md:col-span-5 animate-fade-in-up [animation-delay:120ms]">
          <div
            className="relative overflow-hidden rounded-2xl border border-brand-gold/30 bg-gradient-to-br from-card to-card/40 p-7 shadow-xl"
            data-testid="landing-stat-card"
          >
            <div className="absolute -right-6 -top-6 opacity-[0.08]">
              <Logo size={180} />
            </div>

            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              Statistique d'accroche
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span
                className="font-mono text-5xl font-extrabold tabular-nums text-brand-blue dark:text-brand-gold"
                data-testid="landing-stat-days"
              >
                {daysOfLife.toLocaleString("fr-FR")}
              </span>
              <span className="text-sm font-medium text-muted-foreground">jours de vie</span>
            </div>
            <p className="mt-2 text-sm text-foreground/80">
              Conversion d'un détournement présumé de <span className="font-semibold">1 milliard FCFA</span>
              , pour un contribuable de référence (salaire 200 000 FCFA).
            </p>

            <div className="mt-7 grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-border bg-background/60 p-3">
                <div className="text-xs text-muted-foreground">Pays couverts</div>
                <div className="mt-1 font-mono text-2xl font-bold" data-testid="landing-stat-countries">
                  {countries}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-background/60 p-3">
                <div className="text-xs text-muted-foreground">Docs analysés</div>
                <div className="mt-1 font-mono text-2xl font-bold" data-testid="landing-stat-documents">
                  {documents.toLocaleString("fr-FR")}
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2 text-[11px] text-muted-foreground">
              <BookOpen className="h-3.5 w-3.5" />
              Chiffres illustratifs pré-lancement — pour démonstration académique.
            </div>
          </div>

          {/* Pillars — déplacés sous la stat card */}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              { icon: ScrollText, title: "Audit", desc: "Extraction PDF + LLM sobre (1 appel/document)" },
              { icon: Gavel, title: "Jugement vitaliste", desc: "Simulation pédagogique, avertissement légal obligatoire" },
              { icon: ShieldCheck, title: "Rigueur", desc: "Calculs déterministes, zéro hallucination" },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="rounded-lg border border-border bg-card/60 p-4 backdrop-blur-sm"
              >
                <Icon className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
                <div className="mt-3 font-semibold">{title}</div>
                <div className="mt-1 text-sm text-muted-foreground">{desc}</div>
              </div>
            ))}
          </div>

          {/* Bloc R&D PayPal — sous les piliers */}
          <a
            href="https://www.paypal.com/ncp/payment/94HAJ39UBEHLQ"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="landing-paypal-rnd"
            className="mt-6 block rounded-xl border border-brand-gold/50 bg-gradient-to-br from-amber-50/70 to-card/60 p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:from-amber-900/10"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-brand-blue dark:text-brand-gold">
              <Heart className="h-4 w-4" aria-hidden="true" />
              R&amp;D — Sur la Finance pour la Vie
            </div>
            <div className="mt-2 text-xs leading-relaxed text-foreground/85">
              Contribution à la recherche et au développement (R&amp;D) sur la
              théorie vitaliste. La théorie vitaliste reformule les finances
              publiques autour d'une idée simple et puissante :{" "}
              <em>chaque franc public mal employé ampute une fraction de
              vie humaine</em>. Soutenir cette recherche, c'est aider à
              rendre l'argent public visible — non plus en chiffres, mais en{" "}
              <strong>tranches de vies</strong>.
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue dark:text-brand-gold">
              Soutenir via PayPal
              <ArrowRight className="h-3.5 w-3.5" />
            </div>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border px-6 py-6 text-center text-xs text-muted-foreground md:px-10">
        © {new Date().getFullYear()} VITAE-PUBLICA™ — ELY Mustapha · PMP, I-PMP, IBM Full Stack Developer.
        Outil d'investigation et d'évaluation vitaliste des finances publiques.
      </footer>
    </div>
  );
}
